import {
    ApiBaseService,
    type EnhancedFetch,
    type EnhancedFetchOptions,
    type PaginatedFetch,
    type PaginatedFetchOptions,
} from "./ApiBaseService";
import type { ApiResponse } from "~/types/ApiResponse";
import type { Account } from "~/models/User";
import type {
    ExternalObject,
    ExternalObjectWithAppInfo,
} from "~/models/ExternalObject";
import { faEdit } from "@fortawesome/pro-regular-svg-icons";
import { faStar, faTrash } from "@fortawesome/pro-solid-svg-icons";
import type { SearchParameters } from "ofetch";
import type { Domain } from "~/services/DomainService";

export interface DomainDns {
    id: number;
    external_object_id: number;
    is_verified: boolean;
    name: string;
    host: string;
    record_type: string;
    value: string;
    created_at: string;
    updated_at: string;
}

export interface Domain {
    id: number;
    domain: string;
    is_sendgrid_verified: false;
    default_sender_name: string | null;
    default_sender_email: string | null;
    default_sender_user_id: number | null;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    account: { id: number } | Account;
    external_objects?: ExternalObject[];
    sendgrid_object?: ExternalObjectWithAppInfo;
    dns?: DomainDns[];
}

export default class DomainService extends ApiBaseService {
    public list(
        options?: Partial<PaginatedFetchOptions>,
    ): PaginatedFetch<Domain[]> {
        return this.paginate<Domain[]>(
            this.route("api.domains.index", "/domains"),
            options as PaginatedFetchOptions,
        );
    }

    public find(
        id: number,
        options?: EnhancedFetchOptions,
    ): EnhancedFetch<Domain> {
        return this.enhancedFetch<Domain>(
            this.route("api.domains.show", "/domains/{domain}", {
                domain: id,
            }),
            options,
        );
    }

    public findRequest(
        id: number,
        options?: EnhancedFetchOptions,
    ): Promise<ApiResponse<Domain>> {
        return this.request(
            this.route("api.domains.show", "/domains/{domain}", {
                domain: id,
            }),
            options,
        );
    }

    public update(
        id: number,
        data: Partial<Domain>,
    ): Promise<ApiResponse<Domain>> {
        return this.request(
            this.route("api.domains.store", "/domains/{domain}", {
                domain: id,
            }),
            {
                method: "PATCH",
                body: data,
            },
        );
    }

    public delete(id: number): Promise<null> {
        return this.request(
            this.route("api.domains.destroy", "/domains/{domain}", {
                domain: id,
            }),
            {
                method: "DELETE",
            },
        );
    }

    public async create(value: Partial<Domain>): Promise<Domain> {
        return this.request(this.route("api.domains.store", "/domains"), {
            method: "POST",
            body: value,
        });
    }

    public async senderVerification(
        domain: number,
        value: Partial<{ custom_dkim_selector?: string }> = {},
    ): Promise<Domain> {
        return this.request(
            this.route(
                "api.domains.sender-authentication",
                "/domains/{domain}/sender-authentication",
                {
                    domain,
                },
            ),
            {
                method: "POST",
                body: value,
            },
        );
    }

    public async verifyPublicDomain(
        token: string,
        query?: SearchParameters,
    ): Promise<Domain> {
        return this.request(
            this.route(
                "api.public.domains.dns.verify",
                "/public/domains/dns/{token}/verify",
                {
                    token,
                },
            ),
            {
                method: "POST",
                query,
            },
        );
    }

    public getPublicDomain(
        token: string,
        query: Record<string, string> = {},
    ): EnhancedFetch<Domain> {
        return this.enhancedFetch<Domain>(
            this.route(
                "api.public.domains.dns.show",
                "public/domains/dns/{token}",
                {
                    token,
                },
            ),
            {
                query,
            },
        );
    }

    public async sendToCoworker(domain: number, to: string, message: string) {
        return this.request(
            this.route(
                "api.domains.send-dns-instructions",
                "/domains/{domain}/send-dns-instructions",
                { domain },
            ),
            {
                method: "POST",
                body: { to, message },
            },
        );
    }

    public all(options?: EnhancedFetchOptions): EnhancedFetch<Domain[]> {
        return this.enhancedFetch<Domain[]>(
            this.route("api.domains.all", "/domains/all"),
            options,
        );
    }

    public async verifyDomain(
        domain: Domain,
        options: Partial<{
            onSuccess: (value: Domain[]) => void;
            onCompleted: () => void;
            onError: () => void;
            senderVerificationOptions: Partial<{
                custom_dkim_selector?: string;
            }>;
        }> = {},
    ) {
        const verifications = [];

        if (domain.dns?.find((dns) => dns.name.startsWith("sendgrid_"))) {
            verifications.push(
                this.senderVerification(
                    domain.id,
                    options.senderVerificationOptions ?? {},
                ),
            );
        }

        if (verifications.length) {
            Promise.all(verifications)
                .then((value) => {
                    if (options.onSuccess) options.onSuccess(value);
                })
                .catch(() => {
                    if (options.onError) options.onError();
                })
                .finally(() => {
                    if (options.onCompleted) options.onCompleted();
                });
        } else if (options.onCompleted) options.onCompleted();
    }

    public edit(domain: Domain) {
        useDomain().value = domain;

        navigateTo({
            name: "settings-account-domains-id",
            params: {
                id: domain.id,
            },
        });
    }

    public dropdown(domain: Domain, options: { onDelete?: () => void } = {}) {
        if (!domain) {
            return [];
        }

        const lists: Array<Array<Record<string, any>>> = [
            [
                {
                    label: "Edit",
                    icon: faEdit,
                    click: () => this.edit(domain),
                },
            ],
        ];

        const { user } = useUser();
        const api = useApi();

        if (user.value?.account.domain.id !== domain.id) {
            const setDefaultDomain = (id: number, original_id?: number) => {
                api.account
                    .update({
                        domain_id: id,
                    })
                    .then(async () => {
                        useToast().add(
                            original_id
                                ? {
                                      title: "Default Domain Updated",
                                      description: `${domain.domain} is now your default domain.`,
                                      actions: [
                                          {
                                              label: "Undo",
                                              click: () => {
                                                  setDefaultDomain(
                                                      original_id as number,
                                                  );
                                              },
                                          },
                                      ],
                                  }
                                : {
                                      title: "Default Domain Restored",
                                  },
                        );

                        await useAuth().refreshIdentity();
                    });
            };

            lists[0].push({
                label: "Set Default",
                icon: faStar,
                click: () =>
                    setDefaultDomain(
                        domain.id,
                        user.value?.account?.domain?.id as number,
                    ),
            });
        }

        if (options.onDelete) {
            lists.push([
                {
                    label: "Delete",
                    icon: faTrash,
                    class: "text-red-500",
                    click: () => (options.onDelete as () => void)(),
                },
            ]);
        }

        return lists;
    }
}
